'use client';

import { FC, ReactNode, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { ButtonIcon } from 'src/general/components/Button/ButtonIcon';
import { ScrollBox } from 'src/general/components/ScrollBox/ScrollBox';
import { Fade } from 'src/general/transitions/Fade';
import { Slide } from 'src/general/transitions/Slide';
import { getFilterParamFromCurrentFilters } from 'src/plp/helpers/getPlpUrlWithFilter';
import { plpStore } from 'src/stores/plpStore';
import { debounce } from 'src/utils/debounce';

import { PlpFilters } from '../PlpFilters';
import { FilterHierarchyFieldsGroupOption, FilterOption } from '../PlpFilters.type';
import { FilterOptionFieldsGroup } from '../components/Fields/FilterOptionFieldsGroup';
import { extractFilterOptions, updateCurrentFilterOptions } from '../plpFilterOptions.helper';
import { plpFiltersFormAction } from './PlpFiltersForm.action';

type Props = {
  filters: ReturnType<typeof extractFilterOptions>;
  currentFiltersWithNoResults: FilterOption[];
  driveTrainHelper?: ReactNode;
};

export const PlpFiltersForm: FC<Props> = ({ filters, currentFiltersWithNoResults, driveTrainHelper }) => {
  const { currentFilters, filterParams, setCurrentFilters, setTotalResults, setResultUrl, setIsLoadingResults } =
    plpStore(
      (state) => ({
        totalResults: state.totalResults,
        currentFilters: state.currentFilters,
        setCurrentFilters: state.setCurrentFilters,
        setTotalResults: state.setTotalResults,
        setResultUrl: state.setResultUrl,
        setIsLoadingResults: state.setIsLoadingResults,
        filterParams: state.filterParams,
      }),
      shallow,
    );
  const [selectedHierarchy, setSelectedHierarchy] = useState<{
    option: FilterHierarchyFieldsGroupOption;
    filterTitle: string;
  } | null>(null);

  const onSelectFilter = async (selectedFilter: FilterOption) => {
    setIsLoadingResults(true);
    const updatedFilters = updateCurrentFilterOptions(currentFilters, selectedFilter);
    setCurrentFilters(updatedFilters);
    // Call the action to fetch total vehicles with result url
    const { totalResults, resultUrl } = await plpFiltersFormAction(updatedFilters, filterParams);
    setTotalResults(totalResults);
    setResultUrl(resultUrl);
    setIsLoadingResults(false);
  };

  const onSelectFilterDebounced = debounce(onSelectFilter, 100);
  const filterParam = getFilterParamFromCurrentFilters(currentFilters);
  const modalLayerClassName = 'absolute inset-0 overflow-y-auto overflow-x-hidden bg-bac-tie1';

  return (
    <div className="relative h-full overflow-hidden">
      <ScrollBox data-testid="PlpFilters-root">
        <Fade isVisible={!selectedHierarchy} className={modalLayerClassName}>
          <PlpFilters
            filters={filters}
            filterParam={filterParam}
            currentFilters={currentFilters}
            currentFiltersWithNoResults={currentFiltersWithNoResults}
            driveTrainHelper={driveTrainHelper}
            type="modal"
            onSelectFilter={onSelectFilterDebounced}
            onSelectHierarchy={(filter) => setSelectedHierarchy(filter)}
          />
        </Fade>
        {/* Hierarchical sub-category */}
        <Slide isVisible={!!selectedHierarchy} className={`${modalLayerClassName} flex flex-col`}>
          <header className="flex h-18 shrink-0 items-center gap-x-2 bg-bac-tie1 py-4 pr-4 pl-2">
            <div className="flex w-10 shrink-0 items-center justify-center">
              <ButtonIcon
                title="Back"
                iconName="arrow-left"
                onClick={() => setSelectedHierarchy(null)}
                variant="tertiary"
                size="small"
                styling="round"
              />
            </div>
            {!!selectedHierarchy && <h2 className="c-fs-h5 flex-1 font-extrabold">{selectedHierarchy.filterTitle}</h2>}
          </header>
          <ScrollBox className="flex-1 overflow-y-auto border-t border-bor-neu-mut px-4 py-2">
            {!!selectedHierarchy && (
              <div data-testid={`modal-hierarchy-${selectedHierarchy.filterTitle}`}>
                <FilterOptionFieldsGroup
                  options={selectedHierarchy.option.children!}
                  currentFilters={currentFilters}
                  onSelectFilter={onSelectFilterDebounced}
                  type="modal"
                />
              </div>
            )}
          </ScrollBox>
        </Slide>
      </ScrollBox>
    </div>
  );
};
